import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Bell, CheckCircle, Clock, AlertCircle, Eye } from 'lucide-react';
import { useSelector } from 'react-redux';
import api from '../../api/api';

const CustomerNotifications = () => {
    const { userInfo } = useSelector((state) => state.auth);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all'); // all, unread, read

    // Fetch customer notifications
    const fetchNotifications = useCallback(async () => {
        if (!userInfo) return;
        
        try {
            setLoading(true);
            const response = await api.get('/customer/notifications');
            console.log('Client notification response:', response.data);
            if (response.data && response.data.notifications) {
                console.log('First notification readBy:', response.data.notifications[0]?.readBy);
                console.log('Current userInfo._id:', userInfo._id);
                setNotifications(response.data.notifications);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
            toast.error('Failed to fetch notifications');
        } finally {
            setLoading(false);
        }
    }, [userInfo]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    // Force refresh notifications
    const handleRefresh = () => {
        fetchNotifications();
    };

    // Mark notification as read
    const markAsRead = async (notificationId) => {
        try {
            const response = await api.put(`/customer/notifications/${notificationId}/read`, {
                userType: 'customers'
            });
            
            if (response.data.message) {
                // Update local state to add current user to readBy array
                setNotifications(prev => 
                    prev.map(notification => 
                        notification._id === notificationId 
                            ? { 
                                ...notification, 
                                readBy: [
                                    ...(notification.readBy || []),
                                    { 
                                        userId: userInfo.id || userInfo._id, 
                                        userType: 'customers', 
                                        readAt: new Date(),
                                        _id: new Date().getTime().toString() // Add a temporary ID for consistency
                                    }
                                ]
                            }
                            : notification
                    )
                );
                toast.success('Notification marked as read');
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
            toast.error('Failed to mark notification as read');
        }
    };

    // Check if notification is read by current user
    const isRead = (notification) => {
        if (!notification.readBy || !Array.isArray(notification.readBy)) {
            return false;
        }
        
        // Use userInfo.id instead of userInfo._id for client app
        const currentUserId = userInfo?.id || userInfo?._id;
        
        const read = notification.readBy.some(read => {
            // Handle both string and ObjectId comparisons
            const readUserId = read.userId?.toString() || read.userId;
            const userTypeMatch = read.userType === 'customers';
            const idMatch = readUserId === currentUserId;
            
            return idMatch && userTypeMatch;
        });
        
        return read;
    };

    // Get priority color
    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'bg-red-100 text-red-800';
            case 'medium': return 'bg-yellow-100 text-yellow-800';
            case 'low': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // Get priority icon
    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'high': return <AlertCircle size={16} className="text-red-400" />;
            case 'medium': return <Clock size={16} className="text-yellow-400" />;
            case 'low': return <CheckCircle size={16} className="text-green-400" />;
            default: return <Bell size={16} className="text-gray-400" />;
        }
    };

    // Filter notifications
    const filteredNotifications = notifications.filter(notification => {
        if (filter === 'unread') return !isRead(notification);
        if (filter === 'read') return isRead(notification);
        return true;
    });

    // Get unread count
    const unreadCount = notifications.filter(n => !isRead(n)).length;

    return (
        <div className="px-2 lg:px-7 pt-5">
            <div className="mb-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-[24px] font-bold py-5 text-white flex items-center gap-2">
                            <Bell className="text-[#5EFF00]" size={24} />
                            Notifications
                            {unreadCount > 0 && (
                                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                                    {unreadCount}
                                </span>
                            )}
                        </h1>
                        <p className="text-[#d0d2d6] mt-1">Stay updated with important announcements and updates</p>
                    </div>
                    <div>
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 bg-[#1C1C1C] text-[#d0d2d6] hover:text-white hover:bg-[#2C2C2C] rounded-md transition-colors disabled:opacity-50"
                        >
                            <Bell size={16} className={loading ? 'animate-spin' : ''} />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Filter tabs */}
                <div className="flex space-x-1 bg-[#070707] p-1 rounded-lg mt-4">
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            filter === 'all'
                                ? 'bg-[#5EFF00] text-black'
                                : 'text-[#d0d2d6] hover:text-white hover:bg-[#1C1C1C]'
                        }`}
                    >
                        All ({notifications.length})
                    </button>
                    <button
                        onClick={() => setFilter('unread')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            filter === 'unread'
                                ? 'bg-[#5EFF00] text-black'
                                : 'text-[#d0d2d6] hover:text-white hover:bg-[#1C1C1C]'
                        }`}
                    >
                        Unread ({unreadCount})
                    </button>
                    <button
                        onClick={() => setFilter('read')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            filter === 'read'
                                ? 'bg-[#5EFF00] text-black'
                                : 'text-[#d0d2d6] hover:text-white hover:bg-[#1C1C1C]'
                        }`}
                    >
                        Read ({notifications.length - unreadCount})
                    </button>
                </div>
            </div>

            {/* Notifications List */}
            <div className="bg-[#070707] rounded-lg">
                {loading ? (
                    <div className="p-6 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-[#5EFF00]"></div>
                        <p className="text-[#d0d2d6] mt-2">Loading notifications...</p>
                    </div>
                ) : filteredNotifications.length === 0 ? (
                    <div className="p-6 text-center">
                        <Bell className="mx-auto h-12 w-12 text-[#d0d2d6]" />
                        <h3 className="mt-2 text-sm font-medium text-white">
                            {filter === 'unread' ? 'No unread notifications' : 
                             filter === 'read' ? 'No read notifications' : 'No notifications'}
                        </h3>
                        <p className="mt-1 text-sm text-[#d0d2d6]">
                            {filter === 'all' ? 'You\'ll see notifications from admin here.' : 'Check back later for updates.'}
                        </p>
                    </div>
                ) : (
                    <div className="">
                        {filteredNotifications.map((notification) => {
                            const notificationIsRead = isRead(notification);
                            return (
                            <div
                                key={notification._id}
                                className={`p-6 hover:bg-[#1C1C1C] transition-colors ${
                                    !notificationIsRead ? 'bg-[#0A0A0A]' : ''
                                }`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start space-x-3 flex-1">
                                        <div className="flex-shrink-0 mt-1">
                                            {getPriorityIcon(notification.priority)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h3 className={`text-lg font-medium ${!notificationIsRead ? 'text-white' : 'text-[#d0d2d6]'}`}>
                                                    {notification.title}
                                                </h3>
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(notification.priority)}`}>
                                                    {notification.priority}
                                                </span>
                                                {!notificationIsRead && (
                                                    <span className="w-2 h-2 bg-[#5EFF00] rounded-full"></span>
                                                )}
                                            </div>
                                            <p className={`text-sm mb-3 ${!notificationIsRead ? 'text-[#d0d2d6]' : 'text-gray-500'}`}>
                                                {notification.message}
                                                {notification.source === "payment" && notification.link && <a href={notification.link} className="block underline mt-2">View order payment</a>}
                                            </p>
                                            <div className="flex items-center justify-between">
                                                <p className="text-xs text-gray-500">
                                                    {new Date(notification.createdAt).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </p>
                                                <div className="flex items-center space-x-2">
                                                    {!notificationIsRead && (
                                                        <button
                                                            onClick={() => markAsRead(notification._id)}
                                                            className="flex items-center gap-1 px-3 py-1 text-xs bg-[#5EFF00] text-black hover:bg-[#4CAF50] rounded-md transition-colors"
                                                        >
                                                            <Eye size={12} />
                                                            Mark as read
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomerNotifications;
